import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2, LogOut, Mail, Calendar } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { t } = useLang();
  const { login, logout } = useAuth();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    if (location.state?.user) return;
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, [location.state]);

  if (isAuthenticated === null) {
    return (
      <main className="min-h-screen flex items-center justify-center pt-16" data-testid="account-loading">
        <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
      </main>
    );
  }

  if (!isAuthenticated) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4 pt-16" data-testid="account-signin-prompt">
        <div className="max-w-md w-full rounded-xl border border-slate-700/70 bg-[#1E293B] p-10 text-center">
          <h1 className="font-display text-3xl text-white">{t('account.prompt')}</h1>
          <button
            data-testid="account-google-signin-btn"
            onClick={login}
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-blue-400 hover:shadow-[0_0_30px_rgba(59,130,246,0.45)] active:scale-[0.98] w-full"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" opacity=".9"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
            {t('account.promptBtn')}
          </button>
        </div>
      </main>
    );
  }

  const since = user.created_at ? new Date(user.created_at).toLocaleDateString() : '';

  return (
    <main className="min-h-screen pt-28 pb-24 px-4" data-testid="account-page">
      <div className="max-w-lg mx-auto rounded-xl border border-slate-700/70 bg-[#1E293B] p-10 text-center">
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full mx-auto border-2 border-blue-500/50" data-testid="account-avatar" />
        ) : (
          <div className="w-20 h-20 rounded-full mx-auto bg-blue-500/20 border-2 border-blue-500/50 flex items-center justify-center font-display text-2xl text-blue-300" data-testid="account-avatar">
            {(user.name || user.email || '?').slice(0, 1).toUpperCase()}
          </div>
        )}
        <h1 className="mt-6 font-display text-3xl text-white" data-testid="account-name">{user.name}</h1>
        <p className="mt-2 text-sm text-slate-400 flex items-center justify-center gap-2" data-testid="account-email">
          <Mail className="w-4 h-4" />
          {user.email}
        </p>
        <p className="mt-1.5 text-xs text-slate-500 flex items-center justify-center gap-2">
          <Calendar className="w-3.5 h-3.5" />
          {t('account.since')} {since}
        </p>
        <p className="mt-4 text-xs text-slate-500">{t('account.sub')}</p>
        <button
          data-testid="account-logout-btn"
          onClick={logout}
          className="mt-8 inline-flex items-center justify-center gap-2 rounded-lg border border-slate-600 px-8 py-3.5 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-rose-500/60 hover:text-rose-300 active:scale-[0.98]"
        >
          <LogOut className="w-4 h-4" />
          {t('account.logout')}
        </button>
      </div>
    </main>
  );
}
